/**
 * Planned-order bulk-action eligibility helpers.
 */

/**
 * Normalize a planned-order status to a display string.
 *
 * @param {object|string|undefined} status Status field from the list API.
 * @return {string}
 */
export const getPlannedOrderStatusName = status => {
  if (!status) return "";
  if (typeof status === "string") return status;
  return status.name || status.id || "";
};

/**
 * Whether Confirm should be disabled for the current selection.
 *
 * @param {Array<object>} rows Selected planned-order row objects.
 * @return {boolean}
 */
export const isConfirmDisabledForRows = rows => {
  if (!rows?.length) return true;
  return rows.some(row => {
    const statusName = getPlannedOrderStatusName(row.status);
    return statusName === "Confirmed" || statusName === "Converted";
  });
};

/**
 * Whether Cancel should be disabled for the current selection.
 *
 * @param {Array<object>} rows Selected planned-order row objects.
 * @return {boolean}
 */
export const isCancelDisabledForRows = rows => {
  if (!rows?.length) return true;
  return rows.some(row => {
    const statusName = getPlannedOrderStatusName(row.status);
    return statusName === "Pending" || statusName === "Converted" || statusName === "Cancelled";
  });
};

/**
 * Whether Convert should be disabled for the current selection.
 *
 * @param {Array<object>} rows Selected planned-order row objects.
 * @return {boolean}
 */
export const isConvertDisabledForRows = rows => {
  if (!rows?.length) return true;
  return rows.some(row => getPlannedOrderStatusName(row.status) === "Converted");
};

/**
 * Build Open Draft navigation targets from a convert API response.
 *
 * @param {Array<{type: string, id: number, name?: string}>} createdOrders
 * @return {Array<{path: string, label: string}>}
 */
export const buildOpenDraftLinks = createdOrders => {
  if (!Array.isArray(createdOrders)) return [];
  return createdOrders
    .filter(order => order?.id && (order.type === "purchase" || order.type === "transfer"))
    .map(order => {
      if (order.type === "purchase") {
        return {
          path: `/orders/purchase/${order.id}`,
          label: order.name || `PO ${order.id}`,
        };
      }
      return {
        path: `/transfer_order/${order.id}`,
        label: order.name || `TO ${order.id}`,
      };
    });
};

/**
 * Apply Include Converted to list query params without touching other filters.
 *
 * @param {object} params Current list params.
 * @param {boolean} includeConverted Whether Converted rows should be requested.
 * @return {object} Next params with offset reset to 0.
 */
export const applyIncludeConvertedParams = (params, includeConverted) => {
  const next = { ...(params || {}), offset: 0 };
  if (includeConverted) {
    next.include_converted = true;
  } else {
    delete next.include_converted;
  }
  return next;
};
